import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from "../../environments/environment";

export interface OneKashaAuditPayload {
    activity: string;
    dynamics_order_no?: string | null;
    facility?: string;
    rider_name?: string | null;
    rider_id?: string | null;
    rider_username?: string | null;
    role?: string | null;
    metadata?: Record<string, any>;
    platform_name?: string;
}

@Injectable({
    providedIn: 'root'
})
export class AuditLogsService {
    constructor(private http: HttpClient) {}

    private getPlatformName(): string {
        const orgId = Number(localStorage.getItem('orgId'));
        if (orgId === environment.rw_pdsID || orgId === environment.rw_ncdSanofiID) {
            return 'RW PDS Delivery Form';
        }
        return 'Delivery_Form';
    }

    private buildOneKashaPayload(
        activity: string,
        userEmail: string,
        platformName: string,
        timestamp: string,
        extraData?: Partial<OneKashaAuditPayload>
    ): Record<string, any> {
        const payload: Record<string, any> = {
            timestamp,
            user: userEmail,
            activity,
            platform_name: platformName,
            facility: extraData?.facility || ""
        };
        if (extraData?.dynamics_order_no) payload['order_no'] = extraData.dynamics_order_no;
        if (extraData?.rider_name) payload['rider_name'] = extraData.rider_name;
        if (extraData?.rider_id) payload['rider_id'] = extraData.rider_id;
        if (extraData?.rider_username) payload['rider_username'] = extraData.rider_username;
        if (extraData?.role) payload['role'] = extraData.role;
        if (extraData?.metadata && Object.keys(extraData.metadata).length > 0) {
            payload['metadata'] = extraData.metadata;
        }
        return payload;
    }

    postLog(activity: string, extraData?: Partial<OneKashaAuditPayload>): Observable<any> {
        const userEmail = (localStorage.getItem('email') || '').trim();
        const platformName = extraData?.platform_name || this.getPlatformName();
        const timestamp = new Date().toISOString();

        const legacyData = {
            timestamp,
            user: userEmail || "unknown",
            activity,
            platform_name: platformName,
            facility: extraData?.facility || ""
        };

        const auditUrl = (environment.auditLogUrl || 'https://staging.log-api.kasha.co/collect-log').trim();
        const legacy$ = this.http.post<any>(auditUrl, legacyData).pipe(
            catchError((err) => {
                console.warn('[AuditLogs] /collect-log dispatch failed:', err?.message || err);
                return of(null);
            })
        );

        const shouldDualWrite =
            !!environment.enableOneKashaLog &&
            !!userEmail &&
            userEmail.includes('@');

        if (!shouldDualWrite) {
            return legacy$;
        }

        const oneKashaUrl = (environment.oneKashaLogUrl || '').trim();
        const oneKashaPayload = this.buildOneKashaPayload(
            activity,
            userEmail,
            platformName,
            timestamp,
            extraData
        );
        const oneKasha$ = this.http.post<any>(oneKashaUrl, oneKashaPayload).pipe(
            catchError((err) => {
                console.warn('[AuditLogs] /one-kasha-log dispatch failed:', err?.message || err);
                return of(null);
            })
        );

        return forkJoin({legacy: legacy$, oneKasha: oneKasha$}).pipe(
            map((result) => result.legacy)
        );
    }
}
